/**
 * 오늘 날짜를 KST(한국 표준시) 기준 'YYYY-MM-DD' 문자열로 반환합니다.
 *
 * 브라우저/서버 시간대(UTC, EST 등)와 무관하게 항상 한국 시간 기준으로
 * 오늘 날짜를 생성합니다. `new Date().toISOString().split('T')[0]`는 UTC를
 * 사용하므로 KST 자정~오전 9시 사이에 1일 이전 날짜를 반환하는 버그가
 * 있습니다. 이 헬퍼는 그 문제를 회피합니다.
 *
 * `sv-SE`(스웨덴) 로케일은 `YYYY-MM-DD` 형식을 그대로 출력합니다.
 */
export const getTodayKST = (): string =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });

/**
 * 임의 Date 객체를 KST 기준 'YYYY-MM-DD' 문자열로 변환합니다.
 *
 * Date 객체가 어떤 시간대로 만들어졌든(UTC, KST 등) 그 시점이
 * 한국에서 어느 날짜인지를 반환합니다.
 */
export const formatDateKST = (date: Date): string =>
  date.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });

/**
 * 이용권 만료일(마지막 이용 가능일) 다음 날을 KST 'YYYY-MM-DD' 로 반환합니다.
 *
 * 이어지는 이용권의 시작일을 잡을 때 씁니다. 만료일이 '마지막 이용일'이므로
 * 다음 이용권은 그 다음 날부터 시작해야 하루가 겹치거나 비지 않습니다.
 */
export const nextDayAfter = (isoDate?: string | null): string | undefined => {
  if (!isoDate) return undefined;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setDate(d.getDate() + 1);
  return formatDateKST(d);
};

/**
 * 이용권 만료일 문자열을 KST 기준 'YYYY-MM-DD'(마지막 이용 가능일)로 변환합니다.
 *
 * end_date 는 마지막 이용 가능일 23:59:59(KST)로 저장되고, 백엔드는 UTC 로
 * 직렬화해 내려줍니다("2026-09-08T14:59:59+00:00"). 문자열을 그대로 자르거나
 * "00:00 이 들어있으면 전날" 같은 옛 보정을 하면 **오프셋 `+00:00` 에 걸려**
 * 하루 이르게 나옵니다.
 */
export const expiryDateKST = (value?: string | null): string | null => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return formatDateKST(d);
  // 백엔드가 미리 포맷한 문자열("2026.01.09 (금) 23:59") 대비
  const m = value.match(/(\d{4})[-.](\d{2})[-.](\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

/**
 * ISO 문자열(UTC)을 KST 'YYYY-MM-DD HH:MM' 으로 변환합니다.
 */
export const formatDateTimeKST = (value?: string | null): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' }).slice(0, 16);
};

export const format = (date: Date, formatStr: string): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return formatStr
    .replace('yyyy', String(year))
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};